import { useMutation, useQuery } from '@apollo/client';
import AddIcon from '@mui/icons-material/Add';
import ContrastOutlinedIcon from '@mui/icons-material/ContrastOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import {
    Avatar,
    Badge,
    Box,
    IconButton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useColorScheme,
    useMediaQuery,
} from '@mui/material';
import {
    ButtonComponent,
    DELETE_NOTIFICATION,
    FunctionsEnum,
    UPDATE_SETTINGS_MUTATION,
    getInitials,
} from '@watter/shared-components';
import { userEnums } from '@watter/shared-types';
import { getUrl } from 'aws-amplify/storage';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePermissions } from '../../custom_hooks/permissions';
import { GET_UNREAD_NOTIFICATION_COUNT } from '../../graphql/notifications';
import { GET_USER_PERMISSIONS } from '../../graphql/queries';
import { GetInitialUserSetting } from '../../graphql/query/userSetting';
import { StyleWrapper } from '../../pages/home/home-styles';
import { NotificationOverlay } from '../../pages/notifications/notification-overlay';
import { client } from '../../service/apolloClient';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import {
    setAllNotificationsCleared,
    setUnreadNotificationsCount,
    showNotificationSettingPopUp,
    updateNotificationPopUpPotion,
    updateNotificationSetting,
} from '../../store/reducers/notificationsSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { setUserHasNoAccess, setUserPermissions } from '../../store/reducers/userSlice';
import { NavigationConfig } from '../../utils/navigation-constants';
import { DeleteNotificationPopup } from '../deleteNotificationPopUp/DeleteNotificationPopup';
import DeleteAllNotification from '../icons/delete-all-notification';
import NotificationSettingIcon from '../icons/notification-setting-bell-icon';
import { actionFocus } from '../side-menu/sidemenu-styles';
import { AddAdmin, AddWorkOrder, HeaderStyles, ListWrapper } from './header.style';

interface Iprops {
    openMenuClick: any;
    showIcon: boolean;
    onAddClick: () => void;
}
const HeaderBar: React.FC<Iprops> = ({ openMenuClick, showIcon, onAddClick }) => {
    const isFetchProfileApiCall = useRef(false);
    const navigate = useNavigate();
    const { data: userSettingsData } = useQuery(GetInitialUserSetting);
    const userName = useSelector((state: any) => state.user.userName);
    const userImageLink = useSelector((state: any) => state.user.userImageLink);
    const matches = useMediaQuery(theme => theme.breakpoints.down('lg'));
    const { mode, setMode } = useColorScheme();
    const [updateSettings] = useMutation(UPDATE_SETTINGS_MUTATION);
    const { t } = useTranslation();
    const location = useLocation();
    const [headerContent, setHeaderContent] = useState<any>(null);
    const [currentPath, setLocationPath] = useState<string>('');
    const [profileImageUrl, setProfileImageUrl]: any = useState(null);
    const unreadNotificationCount = useSelector((state: any) => state.notifications.count);
    const [notifyMenuOpen, setNotifyMenuOpen] = useState<HTMLButtonElement | null>(null);
    const dispatch = useDispatch();
    const totalNotificationsCount = useSelector((state: any) => state.notifications.totalNotificationsCount);
    const [openDeleteModal, setDeleteAllModalOpen] = useState<boolean>(false);
    const isLoggedIn = useSelector((state: any) => state.user.isLoggedIn);
    const userHasNoAccess = useSelector((state: any) => state.user.userHasNoAccess);
    const show_notifications = useSelector((state: any) => state.notifications.show_notifications);
    if (userSettingsData?.getUserSettings) {
        let sysMode = userSettingsData?.getUserSettings?.theme;
        switch (sysMode) {
            case userEnums.Theme.auto:
                sysMode = 'system';
                break;
            case userEnums.Theme.dark:
                sysMode = 'dark';
                break;
            case userEnums.Theme.light:
                sysMode = 'light';
                break;
            default:
        }
        setMode(sysMode);
        dispatch(
            updateNotificationSetting({
                show_notifications: userSettingsData?.getUserSettings?.show_notifications,
                allow_notifications: userSettingsData?.getUserSettings?.allow_notifications,
            }),
        );
    }
    const handleUserPermissions = async () => {
        const { data } = await client.query({
            query: GET_USER_PERMISSIONS,
            fetchPolicy: 'network-only',
        });
        const permissions: Record<string, any> = {};
        dispatch(setUserHasNoAccess(false));
        data?.getPermissions?.forEach((item: any) => {
            permissions[item.feature_slug] = {
                view: item.actions[0] === '1',
                create: item.actions[1] === '1',
                edit: item.actions[2] === '1',
                delete: item.actions[3] === '1',
            };
        });
        dispatch(setUserPermissions(permissions));
    };
    React.useEffect(() => {
        handleUserPermissions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userHasNoAccess]);
    const handleThemeChange = async (event: any, newMode: any) => {
        if (newMode !== null) {
            try {
                dispatch(showLoader());
                let updatedMode = newMode;
                switch (newMode) {
                    case 'system':
                        updatedMode = userEnums.Theme.auto;
                        break;
                    case 'dark':
                        updatedMode = userEnums.Theme.dark;
                        break;
                    case 'light':
                        updatedMode = userEnums.Theme.light;
                        break;
                    default:
                }
                const input = {
                    theme: updatedMode,
                };
                const response = await updateSettings({ variables: { input } });
                setMode(updatedMode);
                dispatch(hideLoader());
                dispatch(
                    openToast({
                        show: true,
                        message: response.data.updateSettings?.message,
                        severity: 'success',
                        isAlertOpen: true,
                    }),
                );
                console.warn('Theme settings updated:', response.data.updateSettings);
            } catch (err) {
                console.error('Error updating settings:', err);
                dispatch(hideLoader());
            }
        }
    };

    const fetchImageUrl = useCallback(() => {
        isFetchProfileApiCall.current = true;
        return getUrl({
            path: userImageLink, // Use path instead of key
            options: {
                expiresIn: 3600,
            },
        })
            .then(result => {
                if (result && result.url) {
                    console.warn('Resolved result:', result.url);
                    return result.url;
                }
                console.warn('No URL found in the result:', result);
                return null;
            })
            .catch(err => {
                console.error('Error fetching image:', err);
                return null;
            });
    }, [userImageLink]);
    const setUserProfileImage = useCallback(() => {
        if (userImageLink && !isFetchProfileApiCall.current) {
            const loadImage = async () => {
                const url = await fetchImageUrl();
                if (url) {
                    setProfileImageUrl(url);
                }
            };
            loadImage();
        }
    }, [userImageLink]);
    const handleAddButtonClick = useCallback(() => {
        if (onAddClick) {
            onAddClick();
        } else if (window.handleAddWorkOrder) {
            window.handleAddWorkOrder();
        } else if (window.handleAddAdmin) {
            window.handleAddAdmin();
        } else if (window.handleAddCustomer) {
            window.handleAddCustomer();
        }
    }, [onAddClick]);
    const { canCreate } = usePermissions();
    const getHeaderContext = useCallback(
        (path: string) => {
            switch (path) {
                case NavigationConfig.Home:
                    return (
                        <Box>
                            <Typography variant="h4">{t('dashboard')}</Typography>
                            {/* <Typography variant="h6" display={'flex'} alignItems={'center'} color="info">
                            {' '}
                            {t('heater')}-1 <ArrowDropDownIcon />
                        </Typography> */}
                        </Box>
                    );
                case NavigationConfig.ProfileSetting:
                    return (
                        <Box>
                            <Typography variant="h4">{t('profile_settings')}</Typography>
                            <Typography variant="h6" display="flex" alignItems="center" color="info">
                                {' '}
                            </Typography>
                        </Box>
                    );
                case NavigationConfig.ManageInstaller:
                    return (
                        <Box
                            display="flex"
                            alignItems="center"
                            width="100%"
                            justifyContent="space-between"
                            sx={{
                                '& .MuiIconButton-root': {
                                    '&:active::after': {
                                        ...actionFocus(),
                                        transform: 'scale(1.1, 1.1)',
                                    },
                                },
                            }}
                        >
                            <Typography variant="h4">{t('manage_installer')}</Typography>
                            {showIcon && canCreate(FunctionsEnum.MANAGE_INSTALLERS) && (
                                <AddWorkOrder
                                    disabled={!canCreate(FunctionsEnum.MANAGE_INSTALLERS)}
                                    onClick={handleAddButtonClick}
                                >
                                    <AddIcon />
                                </AddWorkOrder>
                            )}
                        </Box>
                    );
                case NavigationConfig.ManageWorkOrder:
                    return (
                        <Box
                            display="flex"
                            alignItems="center"
                            width="100%"
                            justifyContent="space-between"
                            sx={{
                                '& .MuiIconButton-root': {
                                    '&:active::after': {
                                        ...actionFocus(),
                                        transform: 'scale(1.1, 1.1)',
                                    },
                                },
                            }}
                        >
                            <Typography variant="h4">{t('manage_work_order')}</Typography>
                            {showIcon && canCreate(FunctionsEnum.MANAGE_WORKORDERS) && (
                                <AddWorkOrder
                                    disabled={!canCreate(FunctionsEnum.MANAGE_WORKORDERS)}
                                    onClick={handleAddButtonClick}
                                >
                                    <AddIcon />
                                </AddWorkOrder>
                            )}
                        </Box>
                    );
                case NavigationConfig.ManageCustomer:
                    return (
                        <Box
                            display="flex"
                            alignItems="center"
                            width="100%"
                            justifyContent="space-between"
                            sx={{
                                '& .MuiIconButton-root': {
                                    '&:active::after': {
                                        ...actionFocus(),
                                        transform: 'scale(1.1, 1.1)',
                                    },
                                },
                            }}
                        >
                            <Typography variant="h4">{t('manage_customer')}</Typography>
                            {showIcon && canCreate(FunctionsEnum.MANAGE_CUSTOMERS) && (
                                <AddWorkOrder
                                    disabled={!canCreate(FunctionsEnum.MANAGE_CUSTOMERS)}
                                    onClick={handleAddButtonClick}
                                >
                                    <AddIcon />
                                </AddWorkOrder>
                            )}
                        </Box>
                    );
                case NavigationConfig.InstallerDetails:
                    return (
                        <Box>
                            <Typography variant="h4">{t('installer_details')}</Typography>
                        </Box>
                    );
                case NavigationConfig.ManageAdmin:
                    return (
                        <Box
                            display="flex"
                            alignItems="center"
                            width="100%"
                            justifyContent="space-between"
                            sx={{
                                '& .MuiIconButton-root': {
                                    '&:active::after': {
                                        ...actionFocus(),
                                        transform: 'scale(1.1, 1.1)',
                                    },
                                },
                            }}
                        >
                            <Typography variant="h4">{t('manage_admin')}</Typography>
                            {showIcon && canCreate(FunctionsEnum.MANAGE_ADMINS) && (
                                <AddAdmin
                                    disabled={!canCreate(FunctionsEnum.MANAGE_ADMINS)}
                                    onClick={handleAddButtonClick}
                                >
                                    <AddIcon />
                                </AddAdmin>
                            )}
                        </Box>
                    );
                case NavigationConfig.Notifications:
                    return (
                        <Box>
                            <Typography variant="h4">{t('notifications')}</Typography>
                        </Box>
                    );
                default:
                    return null;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [t, showIcon, handleAddButtonClick],
    );
    React.useEffect(() => {
        const newTitle = getHeaderContext(location.pathname);
        setHeaderContent(newTitle);
        setLocationPath(location.pathname);
        setUserProfileImage();
    }, [location.pathname, userImageLink, getHeaderContext, setUserProfileImage]);

    const open = Boolean(notifyMenuOpen);
    const notifyId = open ? 'simple-popover' : undefined;
    const getUnreadNotificationCount = useCallback(async () => {
        try {
            const { data } = await client.query({
                query: GET_UNREAD_NOTIFICATION_COUNT,
                fetchPolicy: 'network-only',
            });
            if (data && data?.getUnreadNotificationsCount) {
                dispatch(setUnreadNotificationsCount(data?.getUnreadNotificationsCount?.count));
            }
        } catch (error) {
            console.error('Error fetching unread notification count:', error);
        }
    }, [dispatch]); // Removed client from dependencies
    React.useEffect(() => {
        getUnreadNotificationCount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);
    const handleDeleteAllNotifications = () => {
        setDeleteAllModalOpen(true);
    };
    const handleConfirmDeleteAllNotification = async () => {
        try {
            const response = await client.mutate({
                mutation: DELETE_NOTIFICATION,
                variables: {
                    deleteNotificationId: 0, // clears all
                },
            });
            if (response.data?.deleteNotification?.success) {
                dispatch(
                    openToast({
                        show: true,
                        message: response.data?.deleteNotification?.message || t('deleted_notifications_successfully'),
                        severity: 'success',
                        isAlertOpen: true,
                    }),
                );
                dispatch(setAllNotificationsCleared(true));
            } else {
                dispatch(
                    openToast({
                        show: true,
                        message: t('deleted_notifications_failed'),
                        severity: 'error',
                        isAlertOpen: true,
                    }),
                );
            }
        } catch (error) {
            console.error('Error deleting notification:', error);
        } finally {
            setDeleteAllModalOpen(false);
        }
    };
    const getTimeOfDay = () => {
        const hour = new Date().getHours();
        if (hour >= 5 && hour < 12) return t('morning');
        if (hour >= 12 && hour < 17) return t('after_noon');
        if (hour >= 17 && hour < 21) return t('evening');
        return t('night');
    };
    const navigateToViewAllNotifications = () => {
        dispatch(setAllNotificationsCleared(false));
        navigate(NavigationConfig.Notifications);
    };

    const handleSettingClick = (event: React.MouseEvent<HTMLElement>) => {
        dispatch(updateNotificationPopUpPotion(event.currentTarget));
        dispatch(showNotificationSettingPopUp());
    };
    return (
        <>
            <HeaderStyles py={2} direction="row" justifyContent="space-between" alignItems="center">
                {matches && (
                    <IconButton onClick={openMenuClick}>
                        <MenuOutlinedIcon />
                    </IconButton>
                )}
                <Box display={{ xs: 'none', sm: 'block' }}>
                    {currentPath === NavigationConfig.Home && (
                        <>
                            <Typography variant="h4">
                                {t('good')} {getTimeOfDay()}, {userName}!
                            </Typography>
                            <Typography variant="h6">{t('quote1')}</Typography>
                        </>
                    )}
                </Box>

                <Stack direction="row" spacing={1.5} alignItems="center">
                    <ToggleButtonGroup value={mode} exclusive onChange={handleThemeChange} aria-label="text alignment">
                        <ToggleButton value="light" aria-label="light theme">
                            <LightModeOutlinedIcon />
                        </ToggleButton>
                        <ToggleButton value="dark" aria-label="dark theme">
                            <DarkModeOutlinedIcon />
                        </ToggleButton>
                        <ToggleButton value="system" aria-label="system theme">
                            <ContrastOutlinedIcon />
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Badge
                        className="cstBadge"
                        color="secondary"
                        overlap="circular"
                        badgeContent={unreadNotificationCount}
                        variant="dot"
                        invisible={unreadNotificationCount < 0}
                    >
                        <IconButton
                            className="cstNotify"
                            aria-describedby={notifyId}
                            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                if (location.pathname !== NavigationConfig.Notifications)
                                    if (totalNotificationsCount > 0 && show_notifications)
                                        setNotifyMenuOpen(event.currentTarget);
                                    else navigateToViewAllNotifications();
                            }}
                        >
                            <NotificationsNoneOutlinedIcon />
                        </IconButton>
                    </Badge>
                    <ListWrapper
                        open={open}
                        onClose={() => {
                            setNotifyMenuOpen(null);
                        }}
                        id={notifyId}
                        anchorEl={notifyMenuOpen}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <NotificationOverlay
                            onClose={() => {
                                getUnreadNotificationCount();
                                setNotifyMenuOpen(null);
                            }}
                        />
                    </ListWrapper>
                    <Avatar sizes="small" sx={{ display: { xs: 'none', sm: 'flex' } }} src={profileImageUrl}>
                        {getInitials(userName)}
                    </Avatar>
                </Stack>
            </HeaderStyles>
            <StyleWrapper>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between">
                    {headerContent}
                    {/* <Stack direction={'row'} spacing={{ xs: 1, lg: 3 }} justifyContent={'space-between'}>
                        {miniAnalytics(
                            <ElectricalServicesOutlinedIcon sx={{ color: colors.lightBlue }} />,
                            t('power'),
                            t('on'),
                        )}
                        {miniAnalytics(<WifiOutlinedIcon sx={{ color: colors.lightRed }} />, t('internet'), t('off'))}
                        {miniAnalytics(<EmojiEventsOutlinedIcon />, t('score'), '750/1000')}
                    </Stack> */}
                    {location.pathname === NavigationConfig.Notifications && (
                        <Box className="cstNotifyAction " display="flex" alignItems="center" gap={2}>
                            {totalNotificationsCount > 0 && (
                                <HeaderStyles>
                                    <Box className="cstBtnWrapper">
                                        <ButtonComponent
                                            size="medium"
                                            variant="contained"
                                            fullWidth
                                            buttonName={t('clear_all')}
                                            className="cstBtn"
                                            onClick={() => handleDeleteAllNotifications()}
                                        />
                                    </Box>
                                </HeaderStyles>
                            )}
                            <HeaderStyles>
                                <Box className="listItem">
                                    <IconButton
                                        className="cstNotify"
                                        aria-describedby={notifyId}
                                        onClick={handleSettingClick}
                                        data-testid="notification-settings-button"
                                    >
                                        <NotificationSettingIcon />
                                    </IconButton>
                                </Box>
                            </HeaderStyles>
                        </Box>
                    )}
                    <DeleteNotificationPopup
                        open={openDeleteModal}
                        heading={t('clearAllNotifications')}
                        subHeading={t('removeAllNotificationsMsg')}
                        acceptHandler={handleConfirmDeleteAllNotification}
                        rejectHandler={() => setDeleteAllModalOpen(false)}
                        iconComponent={<DeleteAllNotification />}
                        acceptButtonLabel={t('clear_all')}
                        rejectButtonLabel="Cancel"
                    />
                </Stack>
            </StyleWrapper>
        </>
    );
};
export { HeaderBar };
